import {FC, ReactNode, useContext, useRef, useState} from 'react';
import {useForm, SubmitHandler, Controller} from 'react-hook-form';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import Select from 'react-select';
import {toast} from 'react-toastify';

import {Avatar, Button, ButtonGroup, Paper, Stack, Typography} from '@mui/material';
import ControlledInput from '../../../ui/controlled-input/ControlledInput';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import {UserRowOfGroup} from './UserRowOfGroup';

import {useSearchStrategy} from '../../../hooks/useSearchStrategy';
import {searchWorkersStrategy, IWorkerSelectOption} from '../../../api/strategies/searchWorkersStrategy';

import {AccountGroupsService} from '../../../api/services/user-account-groups.service';

import {accountGroupsQueryTrigger} from '../../../store/user-groups/atoms';

import {innerListStyle} from '../../../shared/styles';
import {EventBusContext} from '../../../providers/EventBus';
import {authState} from '../../../store/auth/atoms';
import {IUserAccount} from '../../../shared/types/user-account.types';

interface IAddUserGroup {
  handleClose: () => void;
  loading: boolean;
  setLoading: (arg: boolean) => void;
}

interface IAddUserGroupForm {
  name: string;
  leaderId?: number;
  leaderName?: string;
  managerLogin: string;
  description?: string;
  workers: IWorkerSelectOption<IUserAccount>[];
  preliminaryWorkers: IWorkerSelectOption<IUserAccount>[];
}

const labelStyle = {
  color: '#00203399',
};

export const AddUserGroup: FC<IAddUserGroup> = ({handleClose, loading, setLoading}) => {
  const {user} = useRecoilValue(authState);
  const {control, handleSubmit, setValue, watch, getValues, trigger} = useForm<IAddUserGroupForm>({
    mode: 'onChange',
    defaultValues: {
      preliminaryWorkers: [],
      workers: [],
      managerLogin: user?.userLogin,
      name: '',
    },
  });
  const [openSearch, setOpenSearch] = useState(false);
  const {handleSearch, resetSearch, data, searchLoading} = useSearchStrategy<IWorkerSelectOption<IUserAccount>>(
    searchWorkersStrategy,
    false,
    openSearch,
  );
  const triggerGroupsQuery = useSetRecoilState(accountGroupsQueryTrigger);
  const ref = useRef(null);
  const {bus} = useContext(EventBusContext);

  const usersToGroup = watch('workers');
  const groupName = watch('name');
  const preliminaryUsersToGroup = watch('preliminaryWorkers');
  const leaderId = watch('leaderId');

  const toggleOpenSearch = () => {
    setOpenSearch(!openSearch);
  };

  const fromPreliminaryToFinal = () => {
    setValue('workers', usersToGroup.concat(getValues('preliminaryWorkers')));
    setValue('preliminaryWorkers', []);
  };

  const removeUser = (id: number) => () => {
    setValue(
      'workers',
      getValues('workers').filter((i) => i.value.id !== id),
    );
    leaderId === id && setValue('leaderName', '');
  };

  const assignLeader = (id: number, name: string) => () => {
    if (leaderId === id) {
      setValue('leaderId', 0);
      setValue('leaderName', '');
    } else {
      setValue('leaderId', id);
      setValue('leaderName', name);
      trigger();
    }
  };

  const onSubmit: SubmitHandler<IAddUserGroupForm> = async (data, e) => {
    try {
      setLoading(true);

      const group = await AccountGroupsService.create({
        name: data.name.trim(),
        description: data.description || null,
        leaderId: data.leaderId || null,
        managerLogin: data.managerLogin,
      });

      data.workers.length &&
        (await AccountGroupsService.replaceGroupUsers(
          group.data.id,
          data.workers.map((i) => +i.value.id),
        ));

      toast.success(`Группа "${data.name}" создана`, {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      triggerGroupsQuery((v) => v + 1);

      setTimeout(() => {
        setLoading(false);
        handleClose();
      });
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  const formatOption: (arg: IWorkerSelectOption<IUserAccount>) => ReactNode = ({value, label, photo}) => (
    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
      <Avatar sx={{width: 24, height: 24}} src={photo || ''} />
      <Typography variant="subtitle2">{label}</Typography>
    </Stack>
  );

  return (
    <>
      <form ref={ref} onSubmit={handleSubmit(onSubmit)} style={{height: '100%'}}>
        <Stack direction="column" spacing={1} sx={{justifyContent: 'space-between', height: '100%'}}>
          <Stack direction="column" spacing={2}>
            <Stack direction="row">
              <Typography variant="h5">Сформировать группу</Typography>
            </Stack>
            <Stack direction="column" spacing={0.25} sx={{mt: '32px !important'}}>
              <Typography sx={labelStyle} variant="subtitle2">
                Наименование группы
              </Typography>
              <ControlledInput
                control={control}
                rules={{
                  required: 'Обязательно для заполнения',
                }}
                name="name"
                disabled={loading}
                size="small"
              />
            </Stack>
            <Paper
              sx={{
                ...innerListStyle,
                overflowY: 'auto',
                padding: '3px',
                height: 350,
              }}
            >
              {!!usersToGroup &&
                usersToGroup.map((i) => (
                  <UserRowOfGroup
                    key={i.value.id}
                    user={i}
                    removeUser={removeUser(i.value.id)}
                    assignLeader={assignLeader(i.value.id, i.label)}
                    isLeader={leaderId === i.value.id}
                    loading={loading}
                  />
                ))}
            </Paper>
            <Stack direction="row" sx={{justifyContent: !openSearch ? 'center' : 'normal'}} spacing={2}>
              {!openSearch ? (
                <Button onClick={toggleOpenSearch} size="small" variant="contained">
                  Добавить в группу
                </Button>
              ) : (
                <>
                  <Stack direction="column" sx={{flex: 1, '& > div': {width: '100%'}}} spacing={1.5}>
                    <Controller
                      name="preliminaryWorkers"
                      control={control}
                      render={({field: {onChange, value, ref}}) => (
                        <Select
                          options={data.filter((i) => {
                            const cond1 = !usersToGroup.find((item) => item.value.id === i.value.id);
                            const cond2 = !preliminaryUsersToGroup.find((item) => item.value.id === i.value.id);

                            return cond1 && cond2;
                          })}
                          value={value}
                          isDisabled={loading}
                          isLoading={searchLoading}
                          onInputChange={(newValue) => {
                            newValue.length ? handleSearch(newValue) : resetSearch();
                          }}
                          formatOptionLabel={formatOption}
                          noOptionsMessage={() => 'Ничего не найдено'}
                          loadingMessage={() => 'Поиск...'}
                          onChange={onChange}
                          ref={ref}
                          isMulti
                          placeholder=" "
                        />
                      )}
                    />
                    <Stack direction="row" sx={{justifyContent: 'flex-end'}}>
                      <ButtonGroup variant="contained" size="small">
                        <Button onClick={fromPreliminaryToFinal} disabled={!preliminaryUsersToGroup.length}>
                          Добавить
                        </Button>
                        <Button onClick={toggleOpenSearch} color="error">
                          Отмена
                        </Button>
                      </ButtonGroup>
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
            <Stack direction="column" spacing={0.25} sx={{mt: '20px !important'}}>
              <Typography sx={labelStyle} variant="subtitle2">
                ФИО руководителя
              </Typography>
              <ControlledInput
                control={control}
                rules={{
                  required: usersToGroup.length > 0 && 'Назначьте руководителя из выбранных пользователей',
                }}
                conditionalSxError={{'& fieldset': {borderColor: '#d32f2f !important'}}}
                name="leaderName"
                disabled
                size="small"
              />
            </Stack>
            <Stack direction="column" spacing={0.25}>
              <Typography sx={labelStyle} variant="subtitle2">
                Описание
              </Typography>
              <ControlledInput
                control={control}
                name="description"
                disabled={loading}
                rules={{
                  maxLength: {
                    value: 255,
                    message: 'Максимум 255 символов',
                  },
                }}
                size="small"
                rows={3}
                multiline
              />
            </Stack>
          </Stack>
          <Stack direction="row" sx={{justifyContent: 'space-between', mb: '32px !important'}}>
            <LoadingButton
              onClick={() => {
                (ref?.current as any).requestSubmit();
              }}
              variant="outlined"
              color="success"
              disabled={!groupName.trim().length}
              loading={loading}
            >
              Сформировать
            </LoadingButton>
            <Button onClick={handleClose} variant="outlined" color="error" disabled={loading}>
              Отменить
            </Button>
          </Stack>
        </Stack>
      </form>
    </>
  );
};
